import { Box, Link, Paper, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { Badge, Button } from 'framework/ui/mui-builder';
import { DEFINITION_CATEGORY_LABELS, MIXED_CATEGORY_LABEL } from '../../constants';
import type { DefinitionSummaryDto, FamilySummaryDto, StateKind } from '../../types';
import { formatNumber } from '../../utils/format';
import GapTag, { getGapGroup } from './GapTag';

const COLLAPSED_VARIANTS = 5;
const MAX_VARIANTS_WITHOUT_TOGGLE = 6;

const STATE_SX: Record<StateKind, object> = {
  TRANSIENT: { backgroundColor: 'background.paper' },
  STEADY: { backgroundColor: 'action.hover', borderColor: 'info.main' },
  TERMINAL: { backgroundColor: 'background.default', color: 'text.secondary' },
};

type FamilyCardProps = {
  family: FamilySummaryDto;
  isExpanded: boolean;
  onToggle: (family: string) => void;
  onOpenYaml: (definition: DefinitionSummaryDto) => void;
};

const getGapMessage = (definition: DefinitionSummaryDto, code: string): string =>
  (definition.gapMessages ?? [])
    .filter(message => message.startsWith(`${code}:`))
    .map(message => message.slice(code.length + 2))
    .join('\n');

const DefinitionLink = ({
  definition,
  onOpenYaml,
}: {
  definition: DefinitionSummaryDto;
  onOpenYaml: FamilyCardProps['onOpenYaml'];
}) => (
  <Link
    component="button"
    type="button"
    color="inherit"
    underline="hover"
    title={`${definition.id} — open definition YAML`}
    onClick={() => onOpenYaml(definition)}
    sx={{ font: 'inherit', textAlign: 'left' }}
  >
    {definition.name}
  </Link>
);

export const StateBadge = ({ name, kind, isInitial }: { name: string; kind: StateKind; isInitial?: boolean }) => (
  <Badge
    title={kind.toLowerCase()}
    variant="outlined"
    sx={{ marginLeft: 0, borderColor: 'border.default', whiteSpace: 'nowrap', ...STATE_SX[kind] }}
  >
    {isInitial ? (
      <Box
        component="i"
        sx={{ marginRight: 0.5, fontSize: 10, fontStyle: 'normal', textTransform: 'uppercase', color: 'primary.main' }}
      >
        start
      </Box>
    ) : null}
    {name}
  </Badge>
);

const FamilyCard = ({ family, isExpanded, onToggle, onOpenYaml }: FamilyCardProps) => {
  const definitions = family.definitions;
  const main = definitions[0];
  // Gaps every variant has belong to the family; the others are shown on the variant that has them.
  const common = main.gaps.filter(gap => definitions.every(definition => definition.gaps.includes(gap)));
  const structure = common.filter(gap => getGapGroup(gap) === 'structure');
  const model = common.filter(gap => getGapGroup(gap) !== 'structure');
  const maxInstances = Math.max(1, ...definitions.map(definition => definition.instances));
  const shown =
    isExpanded || definitions.length <= MAX_VARIANTS_WITHOUT_TOGGLE
      ? definitions
      : definitions.slice(0, COLLAPSED_VARIANTS);
  const states = family.stateDetails?.length
    ? family.stateDetails
    : family.states.map(name => ({ key: name, name, kind: 'TRANSIENT' as StateKind, initial: false }));

  return (
    <Paper variant="outlined" sx={{ padding: '12px 14px', marginBottom: 1.5, opacity: family.instances ? 1 : 0.7 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 16px', alignItems: 'baseline' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          <DefinitionLink definition={main} onOpenYaml={onOpenYaml} />
          {definitions.length > 1 ? (
            <Typography component="span" variant="body2" color="text.secondary">
              {' '}
              + {definitions.length - 1} more
            </Typography>
          ) : null}
        </Typography>
        <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          <b>{formatNumber(family.instances)}</b>{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            tickets
          </Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {family.category ? DEFINITION_CATEGORY_LABELS[family.category] : MIXED_CATEGORY_LABEL} ·{' '}
          {family.states.length} states · {family.transitions} transitions · family{' '}
          <Box component="code" title="Fingerprint of states and transitions" sx={{ fontSize: 11 }}>
            {family.family}
          </Box>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
          {formatNumber(family.openInstances)} open
        </Typography>
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center" sx={{ margin: '10px 0 6px' }}>
        {states.map(state => (
          <StateBadge key={state.key ?? state.name} name={state.name} kind={state.kind} isInitial={state.initial} />
        ))}
      </Stack>

      {common.length ? (
        <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center" sx={{ fontSize: 12, marginBottom: 0.5 }}>
          {structure.length ? (
            <>
              <Typography variant="caption" color="text.secondary">
                Problems:
              </Typography>
              {structure.map(code => (
                <GapTag key={code} code={code} title={getGapMessage(main, code)} />
              ))}
            </>
          ) : null}
          {model.length ? (
            <>
              <Typography variant="caption" color="text.secondary">
                {structure.length ? '· Needs:' : 'Needs:'}
              </Typography>
              {model.map(code => (
                <GapTag key={code} code={code} title={getGapMessage(main, code)} />
              ))}
            </>
          ) : null}
        </Stack>
      ) : null}

      {definitions.length > 1 ? (
        <Table size="small" sx={{ '& td': { padding: '5px 8px 5px 0', borderColor: 'border.default' } }}>
          <TableBody>
            {shown.map(definition => {
              const own = definition.gaps.filter(gap => !common.includes(gap));

              return (
                <TableRow key={definition.id} sx={{ color: definition.instances ? 'text.primary' : 'text.secondary' }}>
                  <TableCell sx={{ color: 'inherit' }}>
                    <DefinitionLink definition={definition} onOpenYaml={onOpenYaml} />
                    {definition.category !== family.category ? (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' · '}
                        {DEFINITION_CATEGORY_LABELS[definition.category] || definition.category}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'inherit' }}>
                    {formatNumber(definition.instances)} tickets
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'inherit' }}>
                    {formatNumber(definition.openInstances)} open
                  </TableCell>
                  <TableCell sx={{ width: 120, display: { xs: 'none', sm: 'table-cell' } }}>
                    <Box sx={{ height: 6, borderRadius: 1, overflow: 'hidden', bgcolor: 'background.default' }}>
                      <Box
                        sx={{
                          height: '100%',
                          width: `${Math.round((100 * definition.instances) / maxInstances)}%`,
                          bgcolor: 'primary.main',
                          opacity: 0.7,
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" gap={0.5} justifyContent="flex-end" flexWrap="wrap">
                      {own.map(code => (
                        <GapTag key={code} code={code} title={getGapMessage(definition, code)} />
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : null}

      {definitions.length > MAX_VARIANTS_WITHOUT_TOGGLE ? (
        <Button size="small" variant="text" onClick={() => onToggle(family.family)}>
          {isExpanded ? 'Show fewer' : `Show all ${definitions.length} variants`}
        </Button>
      ) : null}
    </Paper>
  );
};

export default FamilyCard;
